import { Button, ReactIcons } from "@kalyzee/kalyzee-ui";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import styles from "./ActionMenu.module.css";

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

export interface ActionMenuProps {
  label: React.ReactNode;
  items: ActionMenuItem[];
  buttonClassName?: string;
  className?: string;
}

/**
 * Button opening a dropdown list of actions (icon + label).
 * Closes on item click, outside click or Escape.
 */
const ActionMenu = ({ label, items, buttonClassName, className }: ActionMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={clsx(styles.container, className)}>
      <Button.Simple
        className={buttonClassName}
        label={
          <span className={styles.triggerLabel}>
            {label}
            <ReactIcons.ArrowDown className={clsx(styles.triggerChevron, open && styles.triggerChevronOpen)} />
          </span>
        }
        onClick={() => setOpen((o) => !o)}
      />
      {open ? (
        <div className={styles.menu}>
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              className={styles.item}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              <span className={styles.itemIcon}>{item.icon}</span>
              <span className={styles.itemLabel}>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ActionMenu;
